import React from 'react';
import { ThemeContext } from '../../../module/ThemeManager';
import { Container, Box, Grid, Skeleton } from '@mui/material';
import { theme } from '../../../constants/theme';

const CompanyContainerSkeleton: React.FC = () => {
    return (
        <ThemeContext.Consumer>
            {({ themeStyle }) => {
                return (
                    <Container
                        disableGutters
                        style={{
                            height: 'auto',
                            justifyContent: 'flex-start',
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                        sx={{
                            width: '100%',
                            marginLeft: { xs: '0px', sm: '65px' },
                            paddingLeft: { xs: '16px', sm: '40px' },
                            paddingRight: { xs: '16px', sm: '40px' },
                            paddingTop: { xs: '20px', sm: '40px' },
                            overflowX: 'hidden',
                            minHeight: '70vh',
                            backgroundColor: theme[themeStyle].pure_black,
                        }}
                        maxWidth={false}>
                        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                            {/* Header Skeleton */}
                            <Box
                                sx={{
                                    backgroundColor: theme[themeStyle].pure_black,
                                    borderBottom: `1px solid ${theme[themeStyle].tag_white_light}`,
                                    py: 2,
                                    mb: 2,
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 2, sm: 0 },
                                    }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            flexDirection: { xs: 'column', sm: 'row' },
                                        }}>
                                        <Skeleton
                                            variant="circular"
                                            width={64}
                                            height={64}
                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                        />
                                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                            <Skeleton
                                                variant="text"
                                                width={200}
                                                height={40}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 1 }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={150}
                                                height={24}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            flexDirection: { xs: 'column', sm: 'row' },
                                        }}>
                                        <Skeleton
                                            variant="rounded"
                                            width={100}
                                            height={36}
                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                        />
                                        <Skeleton
                                            variant="rounded"
                                            width={100}
                                            height={36}
                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                <Grid item xs={12} lg={8}>
                                    {/* Company Overview Skeleton */}
                                    <Box
                                        sx={{
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                            borderRadius: 2,
                                            mb: 3,
                                            p: 3,
                                        }}>
                                        <Skeleton
                                            variant="text"
                                            width="60%"
                                            height={32}
                                            sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={100}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={140}
                                                height={20}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Skeleton
                                                variant="text"
                                                width="100%"
                                                height={16}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width="90%"
                                                height={16}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width="80%"
                                                height={16}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width="70%"
                                                height={16}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Company Tabs Skeleton */}
                                    <Box
                                        sx={{
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                            borderRadius: 2,
                                        }}>
                                        {/* Tabs Header */}
                                        <Box
                                            sx={{
                                                borderBottom: 1,
                                                borderColor: theme[themeStyle].tag_white_light,
                                                p: 2,
                                            }}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                {Array.from({ length: 6 }).map((_, index) => (
                                                    <Skeleton
                                                        key={index}
                                                        variant="text"
                                                        width={80}
                                                        height={24}
                                                        sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                        {/* Tab Content */}
                                        <Box sx={{ p: 3 }}>
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={400}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    {/* Company Sidebar Skeleton */}
                                    <Box sx={{ spaceY: 3 }}>
                                        {/* Company Stats Skeleton */}
                                        <Box
                                            sx={{
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                                borderRadius: 2,
                                                mb: 3,
                                                p: 3,
                                            }}>
                                            <Skeleton
                                                variant="text"
                                                width="50%"
                                                height={28}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {Array.from({ length: 5 }).map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Skeleton
                                                            variant="text"
                                                            width={80}
                                                            height={20}
                                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                        />
                                                        <Skeleton
                                                            variant="text"
                                                            width={60}
                                                            height={20}
                                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Leadership Skeleton */}
                                        <Box
                                            sx={{
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                                borderRadius: 2,
                                                mb: 3,
                                                p: 3,
                                            }}>
                                            <Skeleton
                                                variant="text"
                                                width="40%"
                                                height={28}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {Array.from({ length: 3 }).map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Skeleton
                                                            variant="circular"
                                                            width={40}
                                                            height={40}
                                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Skeleton
                                                                variant="text"
                                                                width="70%"
                                                                height={20}
                                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                            />
                                                            <Skeleton
                                                                variant="text"
                                                                width="50%"
                                                                height={16}
                                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Similar Companies Skeleton */}
                                        <Box
                                            sx={{
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                                borderRadius: 2,
                                                p: 3,
                                            }}>
                                            <Skeleton
                                                variant="text"
                                                width="60%"
                                                height={28}
                                                sx={{ bgcolor: theme[themeStyle].deep_grey, mb: 3 }}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {Array.from({ length: 3 }).map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={32}
                                                            height={32}
                                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Skeleton
                                                                variant="text"
                                                                width="60%"
                                                                height={20}
                                                                sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                            />
                                                        </Box>
                                                        <Skeleton
                                                            variant="text"
                                                            width={40}
                                                            height={20}
                                                            sx={{ bgcolor: theme[themeStyle].deep_grey }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Container>
                );
            }}
        </ThemeContext.Consumer>
    );
};

export { CompanyContainerSkeleton };
